import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showToast } from 'hfs-common-ui';
import { useViewType } from '~/hooks/use-view-type';
import type { AppState } from '~/reducers';
import { clearMissionOperation } from '~/domain/assets/store/hfs-operations/reducer';
import { useAssetLookup } from '~/domain/assets/hooks/use-asset-lookup';
import formatMessageWrapper from '~/utils/format-message-wrapper/format-message-wrapper';
import type { OperationFailureIndicatorItem } from '~/domain/assets/components/OperationFailureIndicator/types';
import MobileOperationResultIndicatorModal from '~/domain/assets/components/OperationFailureIndicator/MobileOperationResultIndicatorModal';
import {
  getIsMissionOperationFailureIndicationNeeded,
  getMissionOperationSuccessUpdate,
} from '~/domain/roboticmower-details/utils';
import { MissionSettingsUpdateSuccessNotification } from './MissionSettingsUpdateSuccessNotification';

type MissionOperationFailureIndicatorItem = OperationFailureIndicatorItem & {
  missionId?: number;
};

export const MissionSettingsOperationResultIndication = () => {
  const { mobileView } = useViewType();
  const dispatch = useDispatch();
  const { findAsset } = useAssetLookup();
  const [failureDetails, setFailureDetails] = useState<
    MissionOperationFailureIndicatorItem | undefined
  >();
  const allOperations = useSelector((state: AppState) => state.assetHfsOperations.operations);
  const operations = useMemo(
    () => allOperations.filter((operation) => !operation.resultNotificationSent),
    [allOperations]
  );
  const mowingBehaviourSettings = useSelector(
    (state: AppState) => state.assetHfsOperations.mowingBehaviourSettings
  );
  const missionSchedule = useSelector(
    (state: AppState) => state.assetHfsOperations.missionSchedule
  );
  const missionCuttingHeight = useSelector(
    (state: AppState) => state.assetHfsOperations.missionCuttingHeight
  );
  const failedOperations = useMemo(
    () => operations.filter((op) => getIsMissionOperationFailureIndicationNeeded(op, mobileView)),
    [operations, mobileView]
  );
  const successUpdates = useMemo(
    () =>
      operations
        .map((operation) =>
          getMissionOperationSuccessUpdate(
            operation,
            mowingBehaviourSettings,
            missionCuttingHeight,
            missionSchedule
          )
        )
        .filter((update) => update !== undefined),
    [mowingBehaviourSettings, operations, missionCuttingHeight, missionSchedule]
  );

  useEffect(() => {
    if (mobileView) {
      if (failedOperations[0] !== undefined && failureDetails === undefined) {
        setFailureDetails({
          iprId: failedOperations[0].iprId,
          missionId: failedOperations[0].missionId,
          hfsActionId: failedOperations[0].hfsActionId,
          toastTitle: findAsset(failedOperations[0].iprId)?.assetDetails?.name,
          toastMessage: formatMessageWrapper(
            'web.robotic_mower.additional_commands.failure.message'
          ),
          modalTitle: formatMessageWrapper('web.robotic_mower.additional_commands.failure.title'),
          modalMessage: formatMessageWrapper(
            'web.robotic_mower.additional_commands.failure.message'
          ),
        });
      }
    } else {
      for (const commandError of failedOperations) {
        const error = {
          iprId: commandError.iprId,
          hfsActionId: commandError.hfsActionId,
          toastTitle: findAsset(commandError.iprId)?.assetDetails?.name,
          toastMessage: formatMessageWrapper(
            'web.robotic_mower.additional_commands.failure.message'
          ),
          modalTitle: formatMessageWrapper('web.robotic_mower.additional_commands.failure.title'),
          modalMessage: formatMessageWrapper(
            'web.robotic_mower.additional_commands.failure.message'
          ),
        };
        showToast({
          message: error.toastMessage,
          title: error.toastTitle,
          icon: 'notification.error',
          iconColor: 'red500',
          autoClose: 10000,
          position: 'top-right',
        });
        if (commandError?.missionId !== undefined) {
          dispatch(
            clearMissionOperation({
              iprId: commandError.iprId,
              missionId: commandError.missionId,
              hfsActionId: commandError.hfsActionId,
            })
          );
        }
      }
    }
  }, [failedOperations, mobileView, dispatch, failureDetails, findAsset]);

  const dismissCallback = useCallback(() => {
    if (failureDetails?.missionId !== undefined) {
      dispatch(
        clearMissionOperation({
          iprId: failureDetails.iprId,
          missionId: failureDetails.missionId,
          hfsActionId: failureDetails.hfsActionId,
        })
      );
    }
    setFailureDetails(undefined);
  }, [dispatch, failureDetails]);

  if (failureDetails !== undefined) {
    return (
      <MobileOperationResultIndicatorModal
        title={failureDetails.modalTitle}
        iconType="notification.error"
        iconColor="red500"
        message={failureDetails.modalMessage}
        dismissCallback={dismissCallback}
      />
    );
  }

  return (
    <>
      {successUpdates.map((update) => (
        <MissionSettingsUpdateSuccessNotification key={update.operationId} update={update} />
      ))}
    </>
  );
};

export default MissionSettingsOperationResultIndication;
